import {useEffect, useRef, useState} from 'react';

export const useCountdown = (
  countdownTimeInSeconds: number,
  cb?: (currentValue: number) => void,
  duration = 1000
) => {
  const initialTimeRef = useRef(countdownTimeInSeconds);
  const endTimeRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [countdownTime, setCountdownTime] = useState(countdownTimeInSeconds);
  const [running, setRunning] = useState(false);

  const tick = () => {
    if (!endTimeRef.current) return;

    const remainingMs = endTimeRef.current - Date.now();
    const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));

    setCountdownTime(remainingSec);
    cb?.(remainingSec);

    if (remainingSec > 0) {
      timerRef.current = setTimeout(tick, duration);
    } else {
      setRunning(false);
    }
  };

  const startCountdown = () => {
    if (running) return;

    endTimeRef.current = Date.now() + countdownTime * 1000;
    setRunning(true);
    tick();
  };

  const resetCountdown = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    endTimeRef.current = null;
    setRunning(false);
    setCountdownTime(initialTimeRef.current);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {countdownTime, startCountdown, resetCountdown};
};
